import React, { FormEvent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  Container,
  Grid,
  Paper,
  Box,
  Button,
  Select,
  SelectChangeEvent,
  MenuItem,
  TextField,
  InputAdornment, IconButton
} from "@mui/material";
import Layout from "../Layout";
import { fetchRequest } from "../../store/pos/actions";
import { ApplicationState } from "../../store";
import PosTable from "./posTable/PosTable";
import PageHeading from "../../common/pageHeading/PageHeading";
import ProgressIndicator from "../../common/progressIndicator/ProgressIndicator";
import useSession from "../../effects/useSession";
import pageStyles from "../pageStyles";
import CloudDownload from "@mui/icons-material/CloudDownload";
import { Search } from "@mui/icons-material";
import { downloadRequest } from "../../store/pos/actions";
import { IS_DMG_INSTANCE } from "../../lib/utils";
import { downloadLimit } from "../terminal/Terminal";
import CustomizedTooltip from "../../common/toolTip/CustomizedTooltip";
import _ from "lodash";

interface Pagination {
  page: {
    limit: string;
    offset: string;
  };
  filter: { [index: string]: string };
  sort: string;
  cache?: string;
}

export class POSTableParams {
  page = 0;
  pageSize = 1000;
  pageCount = 0;
  orderBy = -1;
  orderDirection = "";
  searchText = "";
  searchField = "attributes.Interface";
  totalCount = 1;
  cache?: string = 'ignore';
}

export const ColumnToRecordMapping = [
  "id",
  "attributes.Name",
  "attributes.MID",
];
export default function Pos(): React.ReactElement {
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = pageStyles();
  const urlSearchParams = new URLSearchParams(history.location.search);
  const onboardingId = urlSearchParams.get('onboardingId') as string;
  let initParams = new POSTableParams();

  const [params, setParams] = useState<POSTableParams>(
    onboardingId ? {
      ...initParams,
      searchField: 'OnboardingID',
      searchText: onboardingId
    } :  initParams
  )

  const [isDownloadAvailable, setIsDownloadAvailable] = useState(false);

  const session = useSession();
  useEffect(() => {
    const p = { ...params, page: 0 };
    refreshData(p);
  }, []);
  const { loading, data } = useSelector((state: ApplicationState) => state.pos);

  const pos = data?.data ?? [];
  const posStoreObjects = data?.meta?.totalObjects ?? 0;

  const refreshData = (newParams: POSTableParams) => {
    const p: Pagination = {
      page: {
        limit: String(newParams.pageSize),
        offset: String(newParams.pageSize * newParams.page),
      },
      filter: {},
      sort: "",
      cache: newParams.cache
    };

    if (newParams.orderBy > -1)
      p.sort =
        (newParams.orderDirection == "desc" ? "-" : "") +
        ColumnToRecordMapping[newParams.orderBy];
    if (newParams.searchText) {
      p.filter[newParams.searchField] = (newParams.searchText || "").trim();
    }
    setParams({ ...newParams });
    dispatch(fetchRequest(null, p));
  };

  const onSearchChange = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const p = { ...params, page: 0 };
    refreshData(p);
  };

  useEffect(()=>{
    if (params.searchText !== '' && posStoreObjects > 0 && posStoreObjects <= downloadLimit) {
      setIsDownloadAvailable(true);
    } else {
      setIsDownloadAvailable(false);
    }
  },[posStoreObjects]);
  const handleOnChange = (event: { target: { value: string } }) => {
    setParams({
      ...params,
      searchText: event.target.value,
      cache: 'ignore',
    });
  };
  const handleDownload = () => {
    const p = {
      page: {
        limit: '99999',
        offset: '0',
      },
      filter: {},
      action:"export",
      cache:"ignore",
      header:["Interface", "TID", "OnboardingID","OnboardedDate"],
      sort: "",
    };
    p.filter[params.searchField] = (params.searchText || "").trim();
    dispatch(downloadRequest( p));
  };

  const downloadButtonTooltip = (storeObjects: number, searchText) => {
    if (_.isEmpty(searchText)) {
      return `Search data to use this feature`;
    } else {
      if (storeObjects === 0) {
        return "You have no item to download";
      } else if (storeObjects > downloadLimit) {
        return `You can download max ${downloadLimit} items while you have ${storeObjects} items in filtered data`;
      } else {
        return `Download for ${storeObjects} items below`;
      }
    }
  }

  return (
    <Layout sessionData={session}>
      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        <Container maxWidth="lg" className={classes.container}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={12} lg={12}>
              <Paper>
                <PageHeading
                  entity="POS"
                  heading="POS"
                  primaryButtonText="Add new POS"
                  primaryButtonClickHandler={() => history.push("/pos/new")}
                />
              </Paper>
              <Box my={2}>
                <Paper>
                  <Box
                    display={"flex"}
                    justifyContent={"space-between"}
                    pb={2}
                    pr={2}
                  >
                    <div></div>
                    <form
                      onSubmit={(e) => onSearchChange(e)}
                      style={{ padding: "1rem" }}
                    >
                      {IS_DMG_INSTANCE &&
                        <CustomizedTooltip
                          title={downloadButtonTooltip(
                            posStoreObjects,
                            params.searchText
                          )}
                        >
                          <Box component="span" marginRight={1}>
                            {/* <Button
                              variant="outlined"
                              size="small"
                              onClick={handleDownload}
                              disabled={!isDownloadAvailable}
                              startIcon={<CloudDownload />}
                            >Download</Button> */}
                          </Box>
                        </CustomizedTooltip>
                      }
                      <Select
                        variant="standard"
                        value={params.searchField}
                        onChange={(event: SelectChangeEvent<string>) => {
                          setParams({
                            ...params,
                            searchField: event.target.value,
                          });
                        }}
                      >
                        <MenuItem value="attributes.Interface" selected={true}>
                          Interface
                        </MenuItem>
                        <MenuItem value="attributes.TID">Paired With</MenuItem>
                        <MenuItem value="attributes.POSDesc">Description</MenuItem>
                        {IS_DMG_INSTANCE && <MenuItem value="OnboardingID">Onboarding Request ID</MenuItem>}
                        {IS_DMG_INSTANCE && <MenuItem value="OnboardedDate">Onboarded Date (DD-MM-YYYY)</MenuItem>}
                      </Select>
                      <TextField
                        style={{ marginTop: "4px", marginLeft: 5, minWidth: "200px", flex: 1 }}
                        placeholder={"Search"}
                        variant="standard"
                        onChange={handleOnChange}
                        value={params.searchText}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton type="submit" edge="end">
                                <Search />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />

                    </form>
                  </Box>
                  {loading ? <ProgressIndicator /> : <PosTable posData={pos} />}
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </main>
    </Layout>
  );
}
